import React, {useEffect, useRef} from 'react'
import {Box, HStack, StackProps, useMultiStyleConfig} from '@chakra-ui/react'
import {MenuItem} from '_uiComponents'
import {useViewport} from '_customHooks'
import {CategoryMenu, MenuType} from '_types'
import EditorialMenu from './partials/editorial'
import CatalogMenu from './partials/catalog'
import MixedMenu from './partials/mixed'

interface SecondLevelMenuProps {
    category: CategoryMenu
    type: MenuType
    menuStaticLibrary: string
    showSecondLevelMenu: number | null
    index: number
    setMenuHeight: React.Dispatch<React.SetStateAction<string>>
    goBackToFirstLevel: () => void
    closeMenu: () => void
    tabIndex?: number
    isAtTop?: boolean
}

const SecondLevelMenu = ({
    category,
    type,
    menuStaticLibrary,
    showSecondLevelMenu,
    index,
    setMenuHeight,
    goBackToFirstLevel,
    closeMenu,
    tabIndex,
    isAtTop = true,
}: SecondLevelMenuProps): JSX.Element => {
    const menuRef = useRef<HTMLDivElement>(null)
    const styles = useMultiStyleConfig('SecondLevelMenu', {isAtTop}) as Record<string, StackProps>
    const {isDesktop} = useViewport()

    const renderMenuType = (): JSX.Element => {
        switch (type) {
            case 'editorial':
                return <EditorialMenu category={category} menuStaticLibrary={menuStaticLibrary} closeMenu={closeMenu} />
            case 'catalog':
                return <CatalogMenu category={category} menuStaticLibrary={menuStaticLibrary} closeMenu={closeMenu} tabIndex={tabIndex} />
            case 'mixed':
                return <MixedMenu category={category} menuStaticLibrary={menuStaticLibrary} closeMenu={closeMenu} tabIndex={tabIndex} />
            default:
                return <></>
        }
    }

    useEffect(() => {
        if (showSecondLevelMenu === index && isDesktop) {
            const menuHeight = menuRef.current?.offsetHeight || 0
            if (menuHeight > 0) {
                setMenuHeight(menuHeight + 46 + 'px')
            } else {
                setMenuHeight(menuHeight + 'px')
            }
        }
    }, [showSecondLevelMenu])

    return (
        <HStack
            {...styles.secondLevel}
            data-testid="second-level-menu"
            ref={menuRef}
            {...(showSecondLevelMenu === index ? styles.open : undefined)}
            onMouseLeave={() => {
                if (isDesktop) {
                    closeMenu()
                }
            }}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    closeMenu()
                }
            }}
            aria-hidden={true}
            id={`menu-${index}`}
        >
            {!isDesktop && (
                <Box
                    {...styles.secondLevelBack}
                    onClick={() => {
                        goBackToFirstLevel()
                    }}
                >
                    <MenuItem href={category.url || '#'} variant="header" renderaslink={isDesktop} arrow={isDesktop ? 'none' : 'left'}>
                        {category.name.toUpperCase()}
                    </MenuItem>
                </Box>
            )}
            {renderMenuType()}
        </HStack>
    )
}

export default SecondLevelMenu
